import type { IdeaStop, IdeaStopType } from '../types/ideaStop.types';

const isBlank = (v: unknown): boolean => v == null || (typeof v === 'string' && !v.trim());

export const TRIP_CONVERSION_MISSING_FIELD_LABELS: Record<string, string> = {
  check_in_date_time: 'Check-in date & time',
  check_out_date_time: 'Check-out date & time',
  start_date_time: 'Start date & time',
  end_date_time: 'End date & time',
  departure_date_time: 'Departure date & time',
  arrival_date_time: 'Arrival date & time',
  flight_from: 'Departure airport (IATA code)',
  flight_to: 'Arrival airport (IATA code)',
};

export type TripConversionStopInput = Partial<IdeaStop> & { stop_type: IdeaStopType };

export const getMissingSendToTripFields = (stop: TripConversionStopInput): string[] => {
  const keys: string[] = [];
  switch (stop.stop_type) {
    case 'Hotel':
      if (isBlank(stop.check_in_date_time)) keys.push('check_in_date_time');
      if (isBlank(stop.check_out_date_time)) keys.push('check_out_date_time');
      break;
    case 'Flight':
      if (isBlank(stop.departure_date_time)) keys.push('departure_date_time');
      if (isBlank(stop.arrival_date_time)) keys.push('arrival_date_time');
      if (isBlank(stop.flight_from)) keys.push('flight_from');
      if (isBlank(stop.flight_to)) keys.push('flight_to');
      break;
    case 'Activity':
    case 'CarRental':
    case 'Concert':
    case 'Theatre':
    case 'Meeting':
    case 'Shopping':
    case 'Restaurant':
    case 'Tour':
      if (isBlank(stop.start_date_time)) keys.push('start_date_time');
      if (isBlank(stop.end_date_time)) keys.push('end_date_time');
      break;
    case 'Parking':
    case 'Directions':
      if (isBlank(stop.start_date_time)) keys.push('start_date_time');
      if (isBlank(stop.end_date_time)) keys.push('end_date_time');
      break;
    case 'Ferry':
    case 'Cruise':
    case 'Rail':
    case 'Transportation':
      if (isBlank(stop.departure_date_time)) keys.push('departure_date_time');
      if (isBlank(stop.arrival_date_time)) keys.push('arrival_date_time');
      break;
    case 'BorderCrossing':
      if (isBlank(stop.start_date_time)) keys.push('start_date_time');
      break;
    default:
      break;
  }
  return keys;
};

export interface IdeaStopTripFieldGap {
  id: number;
  title: string;
  stop_type: IdeaStopType;
  missingKeys: string[];
}

export const collectIdeaStopsMissingTripFields = (
  stops: Array<{ id: number; title?: string; stop_type: IdeaStopType } & Partial<IdeaStop>>
): IdeaStopTripFieldGap[] =>
  stops
    .map(s => ({
      id: s.id,
      title: (s.title ?? s.name ?? 'Stop').trim() || 'Stop',
      stop_type: s.stop_type,
      missingKeys: getMissingSendToTripFields(s),
    }))
    .filter(x => x.missingKeys.length > 0);

export const datetimeLocalToIsoZ = (value: string): string => {
  const t = value?.trim();
  if (!t) return '';
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString();
};

export const extrasSatisfyMissing = (stop: IdeaStop, extras: Record<string, string>): boolean => {
  for (const key of getMissingSendToTripFields(stop)) {
    if (key === 'flight_from' || key === 'flight_to') {
      if (!extras[key]?.trim()) return false;
    } else {
      if (!extras[key]?.trim()) return false;
    }
  }
  return true;
};

export const buildSendIdeaStopToTripBody = (
  stop: IdeaStop,
  extras: Record<string, string>
): Record<string, unknown> => {
  const body: Record<string, unknown> = {};

  if (stop.stop_type === 'Hotel') {
    if (isBlank(stop.check_in_date_time) && extras.check_in_date_time?.trim()) {
      const iso = datetimeLocalToIsoZ(extras.check_in_date_time);
      if (iso) body.check_in_date_time = iso;
    }
    if (isBlank(stop.check_out_date_time) && extras.check_out_date_time?.trim()) {
      const iso = datetimeLocalToIsoZ(extras.check_out_date_time);
      if (iso) body.check_out_date_time = iso;
    }
    return body;
  }

  if (stop.stop_type === 'Activity') {
    if (isBlank(stop.start_date_time) && extras.start_date_time?.trim()) {
      const iso = datetimeLocalToIsoZ(extras.start_date_time);
      if (iso) body.start_date_time = iso;
    }
    if (isBlank(stop.end_date_time) && extras.end_date_time?.trim()) {
      const iso = datetimeLocalToIsoZ(extras.end_date_time);
      if (iso) body.end_date_time = iso;
    }
    return body;
  }

  if (stop.stop_type === 'Flight') {
    const overrides: Record<string, string> = {};
    if (isBlank(stop.departure_date_time) && extras.departure_date_time?.trim()) {
      const iso = datetimeLocalToIsoZ(extras.departure_date_time);
      if (iso) overrides.departure_date_time = iso;
    }
    if (isBlank(stop.arrival_date_time) && extras.arrival_date_time?.trim()) {
      const iso = datetimeLocalToIsoZ(extras.arrival_date_time);
      if (iso) overrides.arrival_date_time = iso;
    }
    if (isBlank(stop.flight_from) && extras.flight_from?.trim()) {
      overrides.flight_from = extras.flight_from.trim().toUpperCase();
    }
    if (isBlank(stop.flight_to) && extras.flight_to?.trim()) {
      overrides.flight_to = extras.flight_to.trim().toUpperCase();
    }
    if (Object.keys(overrides).length) {
      body.overrides = overrides;
    }
    return body;
  }

  return body;
};

export interface SendIdeaStopToTripErrorBody {
  code?: string;
  message?: string;
  trip_name?: string;
  type?: string;
  detail?: string | string[];
  errors?: Array<{
    code?: string;
    detail?: string;
    attr?: string | null;
  }>;
}

/** Fix missing space after period in some API messages (e.g. "duration.Your") */
const prettifyApiSentence = (text: string): string =>
  text
    .trim()
    .replace(/\.([A-Z])/g, '. $1')
    .replace(/\s+/g, ' ');

const collectValidationDetails = (data: unknown): string | null => {
  if (Array.isArray(data)) {
    const parts = data
      .filter((e): e is { detail?: string } => e != null && typeof e === 'object')
      .map(e => (typeof (e as { detail?: string }).detail === 'string' ? (e as { detail: string }).detail.trim() : ''))
      .filter(Boolean)
      .map(prettifyApiSentence);
    if (parts.length) return parts.join(' ');
    return null;
  }
  if (!data || typeof data !== 'object') return null;
  const obj = data as SendIdeaStopToTripErrorBody;
  if (obj.type === 'validation_error' && Array.isArray(obj.errors) && obj.errors.length > 0) {
    const parts = obj.errors
      .map(e => (typeof e?.detail === 'string' ? e.detail.trim() : ''))
      .filter(Boolean)
      .map(prettifyApiSentence);
    if (parts.length) return parts.join(' ');
  }
  if (typeof obj.detail === 'string' && obj.detail.trim()) {
    return prettifyApiSentence(obj.detail);
  }
  if (Array.isArray(obj.detail)) {
    const joined = obj.detail.map(d => (typeof d === 'string' ? d.trim() : '')).filter(Boolean).join(' ');
    if (joined) return prettifyApiSentence(joined);
  }
  return null;
};

export const parseTripMutationApiError = (error: unknown, fallbackMessage: string): string => {
  const raw = (error as { data?: unknown })?.data;
  const fromValidation = collectValidationDetails(raw);
  if (fromValidation) return fromValidation;
  const data = raw as SendIdeaStopToTripErrorBody | undefined;
  if (data?.message) {
    return data.trip_name ? `${data.message} (${data.trip_name})` : data.message;
  }
  if (typeof (error as { error?: string })?.error === 'string') {
    return (error as { error: string }).error;
  }
  return fallbackMessage;
};

export const parseSendIdeaStopToTripError = (error: unknown): string =>
  parseTripMutationApiError(error, 'Could not add this stop to the trip.');

export const validateSendIdeaStopToTripBody = (stop: IdeaStop, extras: Record<string, string>): string | null => {
  const missing = getMissingSendToTripFields(stop);
  if (missing.length === 0) return null;
  if (!extrasSatisfyMissing(stop, extras)) {
    return 'Please fill in all required fields before saving.';
  }
  const body = buildSendIdeaStopToTripBody(stop, extras);
  const overrides = (body.overrides ?? {}) as Record<string, string>;
  const top = body as Record<string, unknown>;

  for (const key of missing) {
    if (stop.stop_type === 'Flight') {
      if (!overrides[key]) {
        return 'Please check flight times and airport codes.';
      }
    } else if (!top[key]) {
      return 'Please check date and time values.';
    }
  }
  return null;
};
